import { isAxiosError } from 'axios'
import { useMemo } from 'react'

import type { ApiClient } from 'clients/yogi-api'
import { useApiClient } from 'clients/yogi-api'
import type {
  ChartsPageState,
  ChartsPageStateRead,
} from 'features/charts-v2/types/yogiql-page-state'

const HASH_FILTER_URL = '/project/hash-filter'
const YOGIQL_PAGE_STATE_URL = '/client/charts/yogiql/page-state'
const HUB_UUID_HEADER = 'X-Hub-UUID'

// ---------------------------------------------------------------------------
// ChartsV2StateService
//
// Feature-scoped state service for Charts V2. Uses ApiClient directly —
// when Charts V2 gets its own persistence endpoint this class changes in isolation.
//
// The state format uses flat v2 types: { panels, globalSettings, activePanelId }
// directly at the root level.
// ---------------------------------------------------------------------------

class ChartsV2StateService {
  constructor(private client: ApiClient) {}

  save(
    projectId: string,
    payload: { page: string; filterString: string },
  ): Promise<string> {
    return this.client
      .post<string>(HASH_FILTER_URL, payload, {
        params: { proj_uuid: projectId },
      })
      .then(({ data }) => data)
  }

  /** Stores the state under `enhanced_dialect`; themes and datasets by name. */
  saveYogiQL(projectId: string, state: ChartsPageState): Promise<string> {
    return this.client
      .post<{ key: string }>(
        YOGIQL_PAGE_STATE_URL,
        { state },
        { headers: { [HUB_UUID_HEADER]: projectId } },
      )
      .then(({ data }) => data.key)
  }

  /** Reads any key, on every hub; an unknown or another hub's key is `not_found`. */
  async loadYogiQL(
    projectId: string,
    key: string,
  ): Promise<ChartsPageStateRead> {
    try {
      const { data } = await this.client.get<ChartsPageStateRead>(
        `${YOGIQL_PAGE_STATE_URL}/${encodeURIComponent(key)}`,
        { headers: { [HUB_UUID_HEADER]: projectId } },
      )
      return data
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return { format: 'not_found' }
      }
      throw error
    }
  }
}

export function useChartsV2StateService(): ChartsV2StateService {
  const client = useApiClient()
  return useMemo(() => new ChartsV2StateService(client), [client])
}
